import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register ChartJS components including annotations plugin
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin
);

interface ROIRampChartProps {
  monthlyProjections: {
    month: number;
    savings: number;
    cumulativeSavings: number;
    investment: number;
    netBenefit: number;
  }[];
  implementationWeeks: number;
  adoptionRampMonths: number;
  paybackMonth: number;
  scenarioMode?: 'conservative' | 'moderate' | 'optimistic';
  currency?: 'USD' | 'EUR';
}

export const ROIRampChart: React.FC<ROIRampChartProps> = ({
  monthlyProjections,
  implementationWeeks,
  adoptionRampMonths,
  paybackMonth,
  scenarioMode = 'moderate',
  currency = 'USD',
}) => {
  const currencySymbol = currency === 'EUR' ? '€' : '$';
  const implementationMonths = Math.ceil(implementationWeeks / 4.33);
  const fullBenefitMonths = Math.max(0, 12 - implementationMonths - adoptionRampMonths);
  
  const chartData = useMemo(() => {
    const labels = monthlyProjections.map(p => `${p.month}mo`);
    
    return {
      labels,
      datasets: [
        // Main cumulative savings line
        {
          type: 'line' as const,
          label: 'Cumulative Savings',
          data: monthlyProjections.map(p => p.cumulativeSavings),
          borderColor: 'rgba(99, 102, 241, 1)',
          backgroundColor: 'rgba(99, 102, 241, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.2,
          pointRadius: 3,
          pointBackgroundColor: 'rgba(99, 102, 241, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          order: 2,
        },
        // Monthly savings bars
        {
          type: 'bar' as const,
          label: 'Monthly Savings',
          data: monthlyProjections.map(p => p.savings),
          backgroundColor: 'rgba(34, 197, 94, 0.6)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 1,
          barPercentage: 0.8,
          categoryPercentage: 0.9,
          yAxisID: 'y1',
          order: 1,
        },
      ],
    };
  }, [monthlyProjections]);

  const options: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: '12-Month ROI Ramp & Payback Timeline',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      legend: {
        display: false, // Disable Chart.js legend - we'll use a custom one
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems.length) return '';
            const monthIndex = tooltipItems[0].dataIndex;
            const monthNum = monthIndex + 1;
            
            // Simple title with phase indicator
            let phaseIcon = '';
            if (monthNum <= implementationMonths) {
              phaseIcon = ' 🔴'; // Implementation
            } else if (monthNum <= implementationMonths + adoptionRampMonths) {
              phaseIcon = ' 🟡'; // Adoption
            } else {
              phaseIcon = ' 🟢'; // Maturity
            }
            
            return `${monthNum}mo${phaseIcon}`;
          },
          label: (context) => {
            const label = context.dataset.label || '';
            const value = Math.round(context.parsed.y);
            
            // Format value with currency
            const formattedValue = `${currencySymbol}${value.toLocaleString()}`;
            
            // Simple, clean labels
            if (label === 'Monthly Savings') {
              return `Monthly: ${formattedValue}`;
            } else if (label === 'Cumulative Savings') {
              // Add Year 1 percentage for cumulative only
              const year1Total = monthlyProjections[11]?.cumulativeSavings || 0;
              const percentage = year1Total > 0 ? Math.round((value / year1Total) * 100) : 0;
              return `Cumulative: ${formattedValue} (${percentage}% of Y1)`;
            } else if (label.includes('Conservative')) {
              return `Conservative: ${formattedValue}`;
            } else if (label.includes('Optimistic')) {
              return `Optimistic: ${formattedValue}`;
            }
            
            return `${label}: ${formattedValue}`;
          },
          afterLabel: (context) => {
            // Only show payback indicator, nothing else
            const monthIndex = context.dataIndex;
            const monthNum = monthIndex + 1;
            
            if (monthNum === paybackMonth && context.dataset.label === 'Cumulative Savings') {
              return '🎯 Payback achieved';
            }
            
            return '';
          },
          footer: () => {
            // Remove footer entirely for simplicity
            return [];
          },
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        padding: 10,
        displayColors: true,
        multiKeyBackground: '#fff',
        titleFont: {
          size: 13,
          weight: 'bold' as const,
        },
        bodyFont: {
          size: 12,
        },
      },
      annotation: {
        annotations: [
          // Background boxes only (no labels since we have a legend)
          ...(implementationMonths > 0 ? [{
            type: 'box' as const,
            xMin: -0.5,
            xMax: implementationMonths - 0.5,
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'transparent',
            drawTime: 'beforeDatasetsDraw' as const,
          }] : []),
          ...(adoptionRampMonths > 0 ? [{
            type: 'box' as const,
            xMin: implementationMonths - 0.5,
            xMax: implementationMonths + adoptionRampMonths - 0.5,
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            borderColor: 'transparent',
            drawTime: 'beforeDatasetsDraw' as const,
          }] : []),
          ...(12 - implementationMonths - adoptionRampMonths > 0 ? [{
            type: 'box' as const,
            xMin: implementationMonths + adoptionRampMonths - 0.5,
            xMax: 11.5,
            backgroundColor: 'rgba(34, 197, 94, 0.05)',
            borderColor: 'transparent',
            drawTime: 'beforeDatasetsDraw' as const,
          }] : []),
          // Payback indicator line
          ...(paybackMonth > 0 && paybackMonth <= 12 ? [{
            type: 'line' as const,
            xMin: paybackMonth - 1,
            xMax: paybackMonth - 1,
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 2,
            borderDash: [5, 5],
            drawTime: 'afterDatasetsDraw' as const,
            label: {
              display: true,
              content: '🎯 Payback',
              position: 'start' as const,
              backgroundColor: 'rgb(99, 102, 241)',
              color: 'white',
              font: {
                size: 12,
                weight: 'bold' as const,
              },
              padding: 6,
              yAdjust: -100,
            },
          }] : []),
        ],
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        grid: {
          display: false,
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: `Cumulative Savings (${currencySymbol})`,
        },
        ticks: {
          callback: (value) => `${currencySymbol}${Math.round(value as number).toLocaleString()}`,
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: `Monthly Savings (${currencySymbol})`,
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: (value) => `${currencySymbol}${Math.round(value as number).toLocaleString()}`,
        },
      },
    },
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div style={{ height: '400px' }}>
        <Chart type="bar" data={chartData} options={options} />
      </div>
      {/* Unified Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        {/* Phase Timeline */}
        <div className="flex items-center justify-center gap-6 text-sm mb-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
            <span className="text-gray-600">Implementation ({implementationMonths}mo)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
            <span className="text-gray-600">Adoption Ramp ({adoptionRampMonths}mo)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
            <span className="text-gray-600">Maturity ({fullBenefitMonths}mo)</span>
          </div>
        </div>
        {/* Data Series */}
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-indigo-500"></div>
            <span className="text-gray-600">Cumulative Savings</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 opacity-60"></div>
            <span className="text-gray-600">Monthly Savings</span>
          </div>
        </div>
      </div>
    </div>
  );
};